/**
 * Seletor em cascata catálogo → área → item de catálogo, limitado aos
 * catálogos vinculados à MESA (GET /desks/:id/catalogs + árvore de cada um
 * via GET /catalogs/:id). Usado no card "Informações do ticket" e no
 * NewTicketDialog — o pai decide exibir o campo com `useDeskCatalogs`.
 */
import { useState } from 'react';
import { useQueries, useQuery } from '@tanstack/react-query';
import type { CatalogDetail } from '@erp/shared';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { cn } from '@/lib/utils';
import { catalogTreeQuery, deskCatalogsQuery } from './tickets-queries';
import { findCatalogPath } from './ticket-sla';

/** Catálogos vinculados à mesa (`enabled` só com mesa definida). */
export function useDeskCatalogs(deskId: number | undefined) {
  return useQuery({
    ...deskCatalogsQuery(deskId ?? 0),
    enabled: deskId !== undefined && deskId > 0,
  });
}

/** Árvores completas dos catálogos informados (cache compartilhado por id). */
export function useCatalogTrees(catalogIds: number[]): CatalogDetail[] {
  return useQueries({
    queries: catalogIds.map((id) => catalogTreeQuery(id)),
    combine: (results) =>
      results
        .map((r) => r.data)
        .filter((tree): tree is CatalogDetail => tree !== undefined),
  });
}

const NONE = 'none';

export function CatalogItemPicker({
  deskId,
  value,
  onChange,
  disabled = false,
  className,
}: {
  deskId: number;
  /** id do item de catálogo selecionado (null = sem item) */
  value: number | null;
  onChange: (itemId: number | null) => void;
  disabled?: boolean;
  className?: string;
}) {
  const catalogsQ = useDeskCatalogs(deskId);
  const catalogs = catalogsQ.data ?? [];
  const trees = useCatalogTrees(catalogs.map((c) => c.id));

  // Seleção local dos níveis superiores; sem interação, deriva do value atual.
  const [localCatalogId, setLocalCatalogId] = useState<number | null>(null);
  const [localAreaId, setLocalAreaId] = useState<number | null>(null);

  const path = value !== null ? findCatalogPath(trees, value) : null;
  const singleCatalogId = trees.length === 1 ? (trees[0]?.id ?? null) : null;
  const catalogId = localCatalogId ?? path?.catalog.id ?? singleCatalogId;
  const catalog = trees.find((t) => t.id === catalogId) ?? null;
  const areaId =
    localAreaId !== null && catalog?.areas.some((a) => a.id === localAreaId)
      ? localAreaId
      : localCatalogId === null
        ? (path?.area.id ?? null)
        : null;
  const area = catalog?.areas.find((a) => a.id === areaId) ?? null;

  const loading = catalogsQ.isPending || trees.length < catalogs.length;

  // Navegar por catálogo/área NÃO limpa o item — só a escolha de um item novo
  // ou o "Sem catálogo"/"Sem item" explícitos disparam onChange.
  const pickCatalog = (v: string) => {
    const next = v === NONE ? null : Number(v);
    setLocalCatalogId(next);
    setLocalAreaId(null);
    if (next === null && value !== null) onChange(null);
  };

  const pickArea = (v: string) => {
    // fixa o catálogo atual para a seleção local não ser sobrescrita pelo path
    setLocalCatalogId(catalogId);
    setLocalAreaId(v === NONE ? null : Number(v));
  };

  const pickItem = (v: string) => {
    onChange(v === NONE ? null : Number(v));
  };

  const itemValue =
    value !== null && area?.items.some((i) => i.id === value) ? String(value) : NONE;

  return (
    <div className={cn('space-y-2', className)}>
      <Select
        value={catalogId !== null ? String(catalogId) : NONE}
        disabled={disabled || loading}
        onValueChange={pickCatalog}
      >
        <SelectTrigger size="sm" className="w-full" aria-label="Catálogo">
          <SelectValue placeholder="Catálogo" />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value={NONE}>
            <span className="text-muted-foreground">Sem catálogo</span>
          </SelectItem>
          {trees.map((t) => (
            <SelectItem key={t.id} value={String(t.id)}>
              {t.name}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>

      <Select
        value={areaId !== null ? String(areaId) : NONE}
        disabled={disabled || loading || !catalog}
        onValueChange={pickArea}
      >
        <SelectTrigger size="sm" className="w-full" aria-label="Área">
          <SelectValue placeholder="Área" />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value={NONE}>
            <span className="text-muted-foreground">Selecione a área…</span>
          </SelectItem>
          {(catalog?.areas ?? [])
            .slice()
            .sort((a, b) => a.position - b.position)
            .map((a) => (
              <SelectItem key={a.id} value={String(a.id)}>
                {a.name}
              </SelectItem>
            ))}
        </SelectContent>
      </Select>

      <Select value={itemValue} disabled={disabled || loading || !area} onValueChange={pickItem}>
        <SelectTrigger size="sm" className="w-full" aria-label="Item de catálogo">
          <SelectValue placeholder="Item" />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value={NONE}>
            <span className="text-muted-foreground">Sem item</span>
          </SelectItem>
          {(area?.items ?? [])
            .slice()
            .sort((a, b) => a.position - b.position)
            .filter((i) => i.active || i.id === value)
            .map((i) => (
              <SelectItem key={i.id} value={String(i.id)}>
                {i.name}
              </SelectItem>
            ))}
        </SelectContent>
      </Select>
    </div>
  );
}
